import api from '../utils/api';
import { withOfflineFallback, readStockLocations } from '../offline';

export interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  location?: string;
  lastOrdered?: string;
  // DEA visibility (PR5, additive; absent on offline replica rows):
  deaMatch?: { substanceId: string; deaSchedule: string } | null;
  deaMatchState?: 'MATCHED' | 'AMBIGUOUS' | 'NONE';
  looksControlled?: boolean;
  lowStockAlert?: number;
  needsPurchase: boolean;
  clinicId: string;
  createdAt: string;
  updatedAt: string;
  // Task 9a (backend, already shipped): the linked ServiceCatalog.basePrice,
  // or null when the item has no catalog link / isn't priced. Not present on
  // the create/update responses (only GET /inventory and GET /inventory/:id).
  catalogPrice?: number | null;
  // Phase 3 (trucks): the StockLocation this row is stocked at (defaults to
  // the clinic's default WAREHOUSE — see recordDispense/recordRestore), the
  // linked ServiceCatalog row (Phase 2 pricing), and per-location restock
  // targets used by the low-stock/restock views.
  locationId?: string;
  catalogItemId?: string;
  reorderPoint?: number;
  parLevel?: number;
}

// Mirrors backend StockLocationType (schema.prisma). TRUCK rows carry an
// ownerVetId (the vet who owns it); WAREHOUSE/SITE do not.
export type StockLocationType = 'WAREHOUSE' | 'TRUCK' | 'SITE';

// Mirrors backend StockLocation (Phase 3, trucks). Pull-only synced entity
// (offline-readable via getLocations); writes are online-only manager actions.
export interface StockLocation {
  id: string;
  clinicId: string;
  name: string;
  type: StockLocationType;
  ownerVetId?: string | null;
  isDefault: boolean;
  isActive: boolean;
}

// Mirrors backend CreateLocationDto. `isDefault` is intentionally NOT a field
// — it can only ever be set by the Phase 0 per-clinic seed.
export interface CreateLocationPayload {
  name: string;
  type: StockLocationType;
  ownerVetId?: string;
  isActive?: boolean;
}

// Mirrors backend UpdateLocationDto — all fields optional (partial update).
export interface UpdateLocationPayload {
  name?: string;
  type?: StockLocationType;
  ownerVetId?: string;
  isActive?: boolean;
}

// Mirrors backend TransferDto (Task 5, trucks).
export interface TransferPayload {
  fromLocationId: string;
  toLocationId: string;
  catalogItemId: string;
  quantity: number;
  /** Omitted = earliest-expiring lots first (ADR-0047). */
  lotNumber?: string;
}

// ── Lots (ADR-0047): mL is the truth, on-hand keeps counting containers ──
export type LotContainerType = 'SINGLE_DOSE' | 'MULTI_DOSE';

/** Mirrors inventory.service LOT_SELECT plus the derived fields listItemLots adds. */
export interface InventoryLot {
  id: string;
  inventoryItemId: string;
  lotNumber: string;
  /** Calendar day (a @db.Date): format with formatCalendarDate, never re-zone. */
  expiresOn: string;
  containerType: LotContainerType;
  mlPerContainer: number;
  /** May be negative: a dose is never refused. */
  mlRemaining: number;
  containersRemaining: number;
  inUseDays: number | null;
  manufacturer: string | null;
  isArchived: boolean;
  /** When the bottle open now was broached (an instant), or null when every bottle is whole. */
  openedAt: string | null;
  createdAt: string;
}

/** GET /inventory/lots: where a dose of this vaccine would come from, and the usable lots there. */
export interface LotsForDispense {
  locationId: string;
  inventoryItemId: string | null;
  onHand: number | null;
  lots: InventoryLot[];
}

/** The lot half of POST /inventory/:id/receive (all or nothing for a new lot number). */
export interface ReceiveLotFields {
  lotNumber?: string;
  /** yyyy-MM-dd */
  expiresOn?: string;
  containerType?: LotContainerType;
  mlPerContainer?: number;
  manufacturer?: string;
  inUseDays?: number;
}

export interface UpdateLotPayload {
  lotNumber?: string;
  expiresOn?: string;
  containerType?: LotContainerType;
  mlPerContainer?: number;
  manufacturer?: string | null;
  inUseDays?: number | null;
  isArchived?: boolean;
}

/** GET /inventory/:id/lot-coverage, in containers like on-hand. */
export interface LotCoverage {
  inventoryItemId: string;
  containersOnShelf: number;
  containersInLots: number;
  unassignedContainers: number;
}

/** Mirrors backend AssignLotDto: `containers` omitted = every container without lot details. */
export interface AssignLotPayload {
  lotNumber: string;
  expiresOn: string;
  containers?: number;
  containerType?: LotContainerType;
  mlPerContainer?: number;
  manufacturer?: string;
  inUseDays?: number;
  idempotencyKey?: string;
}

export interface AssignLotResult {
  /** False for a replay of a submit that already landed. */
  applied: boolean;
  lot: InventoryLot | null;
  assignedContainers: number;
  unassignedContainers: number;
}

// Mirrors backend SetInventoryPricingDto (inventory.controller PUT :id/pricing).
// null/omitted pricingMethod = FIXED (basePrice used as-is); MARKUP derives
// basePrice from unitCost*(1+markupPercent/100), floored at minPrice.
export interface SetInventoryPricingPayload {
  pricingMethod?: 'FIXED' | 'MARKUP';
  basePrice?: number;
  unitCost?: number;
  markupPercent?: number;
  minPrice?: number;
  /** Unit the price is quoted in ("per dose", "per mL"); free text, see utils/billingUnits. */
  unit?: string;
}

export const inventoryService = {
  async getLowStockItems(): Promise<InventoryItem[]> {
    const response = await api.get('/inventory/low-stock');
    return response.data;
  },

  async getAll(params?: { skip?: number; take?: number; category?: string }): Promise<InventoryItem[]> {
    const response = await api.get('/inventory', { params });
    return response.data;
  },

  // Prices a stock item (upserts + links its ServiceCatalog row) so it becomes
  // billable. Online-only, manage:inventory_pricing — callers must gate before calling.
  // The response is the updated item with the linked catalog row nested under
  // `catalogItem` (its resolved basePrice is the price the item now bills at).
  async setPricing(
    id: string,
    payload: SetInventoryPricingPayload,
  ): Promise<InventoryItem & { catalogItem?: { id: string; basePrice: number } | null }> {
    const response = await api.put(`/inventory/${id}/pricing`, payload);
    return response.data;
  },

  // StockLocations (Phase 3, trucks). Offline-aware AT THE SERVICE like
  // serviceCatalogService.getAll — the foundation for the location picker/tab
  // (T8-T11), read-only offline via the synced replica. Inert when the
  // offline flag is off (withOfflineFallback returns the network fn).
  async getLocations(): Promise<StockLocation[]> {
    return withOfflineFallback(
      async () => {
        const response = await api.get('/inventory/locations');
        return response.data;
      },
      () => readStockLocations() as Promise<StockLocation[]>,
    )();
  },

  // Location writes are ONLINE-ONLY manager actions (manage:inventory) —
  // direct api calls, never the offline write queue (mirrors setPricing).
  async createLocation(payload: CreateLocationPayload): Promise<StockLocation> {
    const response = await api.post('/inventory/locations', payload);
    return response.data;
  },

  async updateLocation(id: string, payload: UpdateLocationPayload): Promise<StockLocation> {
    const response = await api.put(`/inventory/locations/${id}`, payload);
    return response.data;
  },

  // Warehouse<->truck transfer (Task 5). Online-only, manage:inventory.
  async transfer(payload: TransferPayload): Promise<unknown> {
    const response = await api.post('/inventory/transfer', payload);
    return response.data;
  },

  // Receive stock (2026-08-21): the explicit restock verb — posts a RECEIVE
  // StockMovement and stamps lastOrdered (= last received). ONLINE-ONLY
  // (StockMovement is never client-pushed, INV-40) — callers gate on
  // !isOffline; never route through the offline write queue.
  async receive(
    id: string,
    payload: { quantity: number; reason?: string; idempotencyKey?: string } & ReceiveLotFields,
  ): Promise<InventoryItem & { lot?: InventoryLot }> {
    const response = await api.post(`/inventory/${id}/receive`, payload);
    return response.data;
  },

  // Lots are online-only like receiving: never replicated, never queued.
  async getItemLots(id: string): Promise<InventoryLot[]> {
    const response = await api.get(`/inventory/${id}/lots`);
    return response.data;
  },

  /** The lots a dose of `name` draws from; no locationId = where this user's dispense resolves (ADR-0013). */
  async getLotsForDispense(name: string, locationId?: string): Promise<LotsForDispense> {
    const response = await api.get('/inventory/lots', { params: { name, ...(locationId ? { locationId } : {}) } });
    return response.data;
  },

  async updateLot(id: string, payload: UpdateLotPayload): Promise<InventoryLot> {
    const response = await api.put(`/inventory/lots/${id}`, payload);
    return response.data;
  },

  /** On-hand no lot accounts for (received without a lot, or from before lots), in containers. */
  async getLotCoverage(id: string): Promise<LotCoverage> {
    const response = await api.get(`/inventory/${id}/lot-coverage`);
    return response.data;
  },

  /** Lot details for containers already on the shelf without them; on-hand does not move. */
  async assignLot(id: string, payload: AssignLotPayload): Promise<AssignLotResult> {
    const response = await api.post(`/inventory/${id}/assign-lot`, payload);
    return response.data;
  },

  /** A recount: the counted mL becomes the lot's balance through a ledgered COUNT. */
  async adjustLot(id: string, payload: { countedMl: number; reason: string; idempotencyKey?: string }): Promise<InventoryLot> {
    const response = await api.post(`/inventory/lots/${id}/adjust`, payload);
    return response.data;
  },

  // Daily location/truck activity report — read-only ledger aggregation,
  // ONLINE-ONLY (StockMovement is never replicated). `from`/`to` are ISO
  // instants computed client-side from the user's local day (the server does
  // no timezone math).
  async getLocationDailyReport(locationId: string, fromIso: string, toIso: string): Promise<LocationDailyReport> {
    const response = await api.get(
      `/inventory/locations/${locationId}/daily-report?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
    );
    return response.data;
  },
};

// ── Daily report shapes (mirror inventory.service.getLocationDailyReport) ──
export type ReportActivityKind = 'DISPENSE' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'VOID_RESTORE' | 'RECEIVE' | 'COUNT' | 'OTHER';

export interface LocationDailyReport {
  location: { id: string; name: string; type: StockLocationType; ownerVetName: string | null };
  from: string;
  to: string;
  generatedAt: string;
  activity: Array<{
    at: string;
    kind: ReportActivityKind;
    typeRaw: string;
    itemName: string;
    /** The amount moved; a COUNT keeps its sign (counted up or down). */
    quantity: number;
    actorName: string | null;
    patientName: string | null;
    reason: string | null;
  }>;
  summary: Array<{
    inventoryItemId: string;
    itemName: string;
    used: number;
    returned: number;
    received: number;
    transferredIn: number;
    transferredOut: number;
    /** Signed sum of counted corrections (ADR-0038); additive, absent from older servers. */
    counted?: number;
    netChange: number;
    onHand: number | null;
    reorderPoint: number | null;
    parLevel: number | null;
  }>;
  restock: Array<{
    inventoryItemId: string;
    itemName: string;
    onHand: number;
    reorderPoint: number | null;
    parLevel: number | null;
    shortfallToPar: number;
  }>;
  totals: { movements: number; totalUsedQty: number; itemsUsed: number };
}

